import { useState, useEffect } from 'react';
import { OrderVersionDto, AnchorPlanFile, OrderFileDto } from '../../../../interfaces/OrderDetail';
import {
  getOrderVersions,
  getOrderDescription,
  extractAnchorPlan,
} from '../../../../axios/orderDetail';

interface UseOrderDetailResult {
  versions: OrderVersionDto[];
  anchorPlan: AnchorPlanFile | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useOrderDetail(soNo: string): UseOrderDetailResult {
  const [versions, setVersions] = useState<OrderVersionDto[]>([]);
  const [anchorPlan, setAnchorPlan] = useState<AnchorPlanFile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);

      try {
        const [versionsData, descriptionsData] = await Promise.all([
          getOrderVersions(soNo),
          getOrderDescription(soNo),
        ]);

        if (cancelled) return;

        setVersions(versionsData);
        setAnchorPlan(extractAnchorPlan(descriptionsData));
      } catch (err: any) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ?? err?.message ?? 'Failed to load order details.'
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [soNo, tick]);

  const refetch = () => setTick((t) => t + 1);

  return { versions, anchorPlan, loading, error, refetch };
}
